import React, { useEffect, useRef, useState } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import "./Treatments.css";
import TreatmentModal from "../AllComponents/Inventory/TreatmentModal";
import ConfirmDeleteModal from "../AllComponents/Inventory/ConfirmDeleteModal";
import PaginationComponent from "../AllComponents/Shared/PaginationComponent";
import {
  getTreatments,
  searchTreatments,
  createTreatment as apiCreateTreatment,
  updateTreatment as apiUpdateTreatment,
  deleteTreatment as apiDeleteTreatment,
} from "../../api/admin/inventory";

// Normalize treatment: map backend fields (cost) to frontend fields (price)
const normalizeTreatment = (t) => {
  if (!t) return t;
  const id = t.id || t._id || t.treatmentId || t.serviceId;
  const normalized = id ? { ...t, id } : t;
  // Map backend fields to frontend fields
  if (normalized.cost !== undefined && normalized.price === undefined) {
    normalized.price = Number(normalized.cost);
  }
  // Ensure name exists (fallback to empty string if missing)
  if (!normalized.name) {
    normalized.name = "";
  }
  // Ensure price is a number
  if (normalized.price !== undefined) {
    normalized.price = Number(normalized.price);
  }
  return normalized;
};

// Convert frontend fields (price) to backend fields (cost)
const toBackendFormat = (data) => {
  const backend = { name: data.name };
  if (data.price !== undefined) backend.cost = data.price;
  return backend;
};

const Treatments = () => {
  const [search, setSearch] = useState("");
  const [treatments, setTreatments] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const limitOptions = [10, 20, 30, 50, 100];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchTimeoutRef = useRef(null);

  const effectiveList =
    search.trim() && !loading ? searchResults : treatments;
  const filtered = effectiveList;
  const totalItems = treatments.length;

  // Reusable function to fetch all treatments
  const fetchTreatments = async () => {
    setLoading(true);
    try {
      const { data } = await getTreatments();
      const list = Array.isArray(data) ? data : data?.treatments || data?.result || data?.services || [];
      setTreatments(list.map(normalizeTreatment));
      setError(null);
    } catch (err) {
      console.error("Failed to load treatments", err);
      setError("Failed to load treatments.");
      toast.error("Failed to load treatments. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch treatments on mount
  useEffect(() => {
    fetchTreatments();
  }, []);

  const handleAdd = () => {
    setEditingTreatment(null);
    setIsModalOpen(true);
  };

  const handleEdit = (id, event) => {
    const trt = treatments.find((t) => t.id === id);
    if (!trt) return;
    setEditingTreatment(trt);
    setIsModalOpen(true);
  };

  const handleDeleteRequest = (id, event) => {
    const trt = treatments.find((t) => t.id === id);
    if (!trt) return;
    setDeleteTarget(trt);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await apiDeleteTreatment(deleteTarget.id || deleteTarget._id);
      // Optimistically remove from both full list and current search results
      setTreatments((prev) =>
        prev.filter(
          (t) => t.id !== (deleteTarget.id || deleteTarget._id)
        )
      );
      setSearchResults((prev) =>
        prev.filter(
          (t) => t.id !== (deleteTarget.id || deleteTarget._id)
        )
      );
      toast.success("Treatment deleted successfully!");
    } catch (err) {
      console.error("Failed to delete treatment", err);
      setError("Failed to delete treatment.");
      toast.error("Failed to delete treatment. Please try again.");
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  // Reset to first page when search term changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  // Debounced search effect for treatments
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const query = search.trim();

    if (!query) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const { data } = await searchTreatments(query);
        // Support multiple possible response shapes:
        // - array directly
        // - { treatments: [...] }
        // - { result: [...] }
        // - { results: [...] }
        // - { services: [...] }
        // - single object
        let raw =
          Array.isArray(data)
            ? data
            : data?.treatments ||
              data?.result ||
              data?.results ||
              data?.services ||
              data;
        const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
        setSearchResults(list.map(normalizeTreatment));
      } catch (err) {
        console.error("Failed to search treatments", err);
        // No error toast for search, just clear results
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 800);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  // Reset to first page when page size changes
  useEffect(() => {
    setPage(1);
  }, [limit]);

  const startIndex = (page - 1) * limit;
  const pageItems = filtered.slice(startIndex, startIndex + limit);

  const handleSaveFromModal = async (payload) => {
    try {
      // Convert frontend format (price) to backend format (cost)
      const backendPayload = toBackendFormat(payload);

      if (editingTreatment) {
        // Call backend API to update
        await apiUpdateTreatment(
          editingTreatment.id || editingTreatment._id,
          backendPayload
        );
        toast.success("Treatment updated successfully!");
      } else {
        // Call backend API to create
        await apiCreateTreatment(backendPayload);
        toast.success("Treatment added successfully!");
      }

      // Always refetch full list from backend
      await fetchTreatments();

      // If user is searching, also refresh the current search results from backend
      const query = search.trim();
      if (query) {
        try {
          const { data } = await searchTreatments(query);
          let raw =
            Array.isArray(data)
              ? data
              : data?.treatments ||
                data?.result ||
                data?.results ||
                data?.services ||
                data;
          const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
          setSearchResults(list.map(normalizeTreatment));
        } catch (searchErr) {
          console.error("Failed to refresh search treatments", searchErr);
          // keep previous searchResults if refresh fails
        }
      }

      setIsModalOpen(false);
      setEditingTreatment(null);
    } catch (err) {
      console.error("Failed to save treatment", err);
      setError("Failed to save treatment.");
      toast.error("Failed to save treatment. Please try again.");
      // Refetch on error to ensure consistency
      await fetchTreatments();
    }
  };

  return (
    <div className="trt-surface">
      <div className="trt-shell">
        <header className="trt-hero">
          <div className="trt-hero-copy">
            <p className="trt-chip">Treatment ops</p>
            <h1>
              Treatment catalogue
              <span> at a glance.</span>
            </h1>
          </div>
        </header>

        <section className="trt-search-block">
          <div className="trt-search-field">
            <svg
              className="trt-search-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
            <input
              type="text"
              placeholder="Search treatment by name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <span className="trt-count">
            {search
              ? `Showing ${filtered.length} of ${totalItems} treatments`
              : `All treatments (${totalItems})`}
          </span>
        </section>

        <section className="trt-actions-bar">
          <button className="trt-primary" type="button" onClick={handleAdd}>
            <span>+</span> Add treatment
          </button>
        </section>

        <section className="trt-table-wrapper">
          <table className="trt-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Price (₹)</th>
                <th className="trt-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading || searchLoading ? (
                Array.from({ length: limit }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="trt-skeleton-row">
                    <td>
                      <div className="trt-skeleton-cell"></div>
                    </td>
                    <td>
                      <div className="trt-skeleton-cell"></div>
                    </td>
                    <td className="trt-actions">
                      <div className="trt-skeleton-actions">
                        <div className="trt-skeleton-button"></div>
                        <div className="trt-skeleton-button"></div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={3} className="trt-empty">
                    No treatments match your search.
                  </td>
                </tr>
              ) : (
                pageItems.map((trt) => (
                  <tr key={trt.id}>
                    <td>{trt.name}</td>
                    <td>{trt.price}</td>
                    <td className="trt-actions">
                      <button
                        type="button"
                        className="trt-btn trt-btn-edit"
                        onClick={(e) => handleEdit(trt.id, e)}
                        aria-label="Edit treatment"
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className="trt-btn trt-btn-delete"
                        onClick={(e) => handleDeleteRequest(trt.id, e)}
                        aria-label="Delete treatment"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>

        {filtered.length > 10  && (
          <PaginationComponent
            total={filtered.length}
            limit={limit}
            limitOptions={limitOptions}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={(nextLimit) => {
              setLimit(nextLimit);
              setPage(1);
            }}
            className="trt-pagination"
          />
        )}

        <TreatmentModal
          isOpen={isModalOpen}
          mode={editingTreatment ? "edit" : "add"}
          initialValues={editingTreatment || undefined}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTreatment(null);
          }}
          onSave={handleSaveFromModal}
        />

        <ConfirmDeleteModal
          isOpen={!!deleteTarget}
          title="Delete treatment"
          message={
            deleteTarget
              ? `Are you sure you want to delete "${deleteTarget.name}"? This cannot be undone.`
              : ""
          }
          onCancel={() => {
            if (isDeleting) return;
            setDeleteTarget(null);
          }}
          onConfirm={handleConfirmDelete}
          confirming={isDeleting}
        />
      </div>
    </div>
  );
};

export default Treatments;